import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import propTypes from '../../../node_modules/prop-types/index.js';
import { KEY_CODE } from '../constants.js';
import Errors from '../Errors/Errors.js';
import Label from '../Label/Label.js';
import { safeRest, hasErrors, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { DROPDOWN_VARIANT } from './constants.js';
import DropdownInput from './DropdownInput.js';
import DropdownMenu from './DropdownMenu.js';
import DropdownSelect from './DropdownSelect.js';
import { Root, StyledDropdown } from './styledComponents.js';
import { makeMenuItems, getSelectedIndex, getSelectedItemDetails, buildHandler } from './utils.js';
import useTheme from '../../hooks/useTheme.js';
import useToggle from '../../hooks/useToggle.js';

var _excluded = ["id", "callouts", "dropdownItems", "selected", "value", "placeholder", "disabled", "errors", "outline", "label", "ariaRequired", "ariaLabel", "ariaLabelledBy", "ariaDescribedBy", "onChange", "variant", "customStyle"];
/**
 * Partially controlled Dropdown component.
 * Visually selected item is controlled internally by state.
 *
 * Enter
 * If the focus is on the button, expands the listbox and places focus on the currently selected option in the list.
 * If focus is in the listbox , collapses the listbox and keeps the currently selected option as the button label.
 *
 * Escape
 * If the listbox is displayed, collapses the listbox and moves focus to the button.
 *
 * Down Arrow
 * Moves focus to and selects the next option.
 * If the listbox is collapsed, also expands the list.
 *
 * Up Arrow
 * Moves focus to and selects the previous option.
 * If the listbox is collapsed, also expands the list.
 *
 * Home
 * If the listbox is displayed, moves focus to and selects the first option.
 *
 * End
 * If the listbox is displayed, moves focus to and selects the last option.
 *
 * Details (here)[https://www.w3.org/TR/wai-aria-practices/examples/listbox/listbox-collapsible.html]
 */

var Dropdown = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var id = _ref.id,
      callouts = _ref.callouts,
      dropdownItems = _ref.dropdownItems,
      selected = _ref.selected,
      value = _ref.value,
      placeholder = _ref.placeholder,
      disabled = _ref.disabled,
      errors = _ref.errors,
      outline = _ref.outline,
      label = _ref.label,
      ariaRequired = _ref.ariaRequired,
      ariaLabel = _ref.ariaLabel,
      ariaLabelledBy = _ref.ariaLabelledBy,
      ariaDescribedBy = _ref.ariaDescribedBy,
      onChange = _ref.onChange,
      variant = _ref.variant,
      customStyle = _ref.customStyle,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useTheme = useTheme(),
      dropdown = _useTheme.dropdown;

  var native = dropdown.native;
  var safeProps = safeRest(rest);

  var _safeCustomStyle = safeCustomStyle(customStyle),
      rootStyle = _safeCustomStyle.rootStyle;

  var items = makeMenuItems(dropdownItems, placeholder, native);

  var _useToggle = useToggle(false),
      _useToggle2 = _slicedToArray(_useToggle, 3),
      expanded = _useToggle2[0],
      toggleExpanded = _useToggle2[1],
      setExpanded = _useToggle2[2];

  var _useState = useState(getSelectedIndex(items, value, selected)),
      _useState2 = _slicedToArray(_useState, 2),
      selectedIndex = _useState2[0],
      setSelectedIndex = _useState2[1];

  var hasError = hasErrors(errors);
  var errorsId = hasErrors(errors) ? "".concat(id, "-errors") : ariaDescribedBy;
  useEffect(function () {
    setSelectedIndex(getSelectedIndex(items, value, selected));
  }, [items, selected, value]); // Only re-run the effect if selected prop changes

  var dropDownFocus = useRef(null); // memoized change handler

  var handleChange = useCallback(function (event, ind) {
    if (native && getSelectedIndex(items, event.target.value) >= 0) {
      setSelectedIndex(getSelectedIndex(items, event.target.value));
      handle(onChange)(event, event.target.value);
    } else if (items.length > ind && !items[ind].disabled) {
      setSelectedIndex(ind);
      handle(onChange)(event, items[ind].value); // callback with actual value of selected item
    } else ;
  }, [native, items, onChange]); // memoized click handler for option select

  var handleItemClick = useCallback(function (event, ind) {
    handleChange(event, ind);

    if (!native) {
      toggleExpanded();
      dropDownFocus.current.focus();
    }
  }, [native, handleChange, toggleExpanded]);
  var handleInputClick = useCallback(function () {
    return toggleExpanded();
  }, [toggleExpanded]);

  var handleKeyboardEvent = function handleKeyboardEvent(e) {
    var key = e.key,
        keyCode = e.keyCode;
    var handler = buildHandler(e, items, selectedIndex, handleChange, setExpanded, toggleExpanded);

    if (handler[key]) {
      e.preventDefault(); // only prevent default if part of bound keys

      handler[key]();
    }

    if (keyCode === KEY_CODE.ENTER || keyCode === KEY_CODE.SPACE) {
      dropDownFocus.current.focus();
    }
  };

  var handleListboxBlur = function handleListboxBlur() {
    return setTimeout(function () {
      setExpanded(false);
    }, 100);
  };

  var selectedItem = getSelectedItemDetails(items, selectedIndex);
  var placeholderText = selectedItem.label;
  return /*#__PURE__*/React.createElement(Root, {
    rootStyle: rootStyle
  }, !native && /*#__PURE__*/React.createElement("input", _extends({}, safeProps, {
    id: id,
    type: "hidden",
    value: value || ''
  })), label && /*#__PURE__*/React.createElement(Label, {
    id: "".concat(id, "-label"),
    htmlFor: "".concat(id, "-toggle"),
    customStyle: {
      labelStyle: customStyle.labelStyle
    }
  }, label), /*#__PURE__*/React.createElement(StyledDropdown, null, native ? /*#__PURE__*/React.createElement(DropdownSelect, _extends({}, safeProps, {
    id: id,
    label: label,
    dropdownItems: items,
    selected: selectedIndex,
    ariaLabel: ariaLabel,
    ariaLabelledBy: ariaLabelledBy,
    ariaRequired: ariaRequired,
    ariaDescribedBy: errorsId,
    placeholder: placeholder,
    outline: outline || hasError,
    disabled: disabled,
    handleItemClick: handleItemClick,
    variant: variant,
    customStyle: {
      dropdownInputStyle: customStyle.dropdownInputStyle
    },
    ref: ref
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DropdownInput, {
    id: id,
    label: label,
    ariaLabel: ariaLabel,
    ariaLabelledBy: ariaLabelledBy,
    ariaDescribedBy: errorsId,
    placeholderText: placeholderText,
    ariaRequired: ariaRequired,
    expanded: expanded,
    outline: outline || hasError,
    disabled: disabled,
    handleKeyboardEvent: handleKeyboardEvent,
    handleInputClick: handleInputClick,
    variant: variant,
    customStyle: {
      dropdownInputStyle: customStyle.dropdownInputStyle
    },
    tabIndex: "-1",
    ref: dropDownFocus
  }), expanded && /*#__PURE__*/React.createElement(DropdownMenu, {
    id: id,
    dropdownItems: items,
    placeholder: placeholder,
    ariaRequired: ariaRequired,
    handleListboxBlur: handleListboxBlur,
    handleKeyboardEvent: handleKeyboardEvent,
    handleItemClick: handleItemClick,
    selected: selectedIndex,
    customStyle: {
      dropdownMenuStyle: customStyle.dropdownMenuStyle
    }
  })), /*#__PURE__*/React.createElement(Errors, {
    id: id,
    callouts: callouts,
    errors: errors
  })));
});
process.env.NODE_ENV !== "production" ? Dropdown.reduxFormProps = {
  inputProp: 'value',
  inputType: 'string'
} : void 0;
process.env.NODE_ENV !== "production" ? Dropdown.propTypes = {
  /** DOM element id */
  id: propTypes.string,

  /** label component for input */
  label: propTypes.node,

  /** global attribute aria-required */
  ariaRequired: propTypes.bool,

  /** override input aria-label */
  ariaLabel: propTypes.string,

  /** override input aria-labelledby */
  ariaLabelledBy: propTypes.string,

  /** DOM element ID for aria-describedBy on input */
  ariaDescribedBy: propTypes.string,

  /** Dropdown menu items */
  dropdownItems: propTypes.arrayOf(propTypes.shape({
    /** value for the item, equivalent to option value in select element */
    value: propTypes.string,

    /** label for the item, equivalent to option label in select element */
    label: propTypes.string.isRequired,

    /** Item is not selectable if it is disabled */
    disabled: propTypes.bool
  })),

  /** display as a disabled item place at the top, selected by default */
  placeholder: propTypes.string,

  /** sets aria-live to 'polite' and role tp 'alert' on error message block  */
  callouts: propTypes.bool,

  /** index of selected item, default is 0 */
  selected: propTypes.number,

  /** selected item by value */
  value: propTypes.string,

  /** HTML 5 disabled attribute */
  disabled: propTypes.bool,

  /** onChange handler for click and key events on dropdown menu */
  onChange: propTypes.func,

  /** error array */
  errors: propTypes.arrayOf(propTypes.shape({
    id: propTypes.string,
    // error id
    msg: propTypes.any,
    // localized error text
    isError: propTypes.bool // error condition

  })),

  /** use error styling without displaying errors; useful if your app needs to handle errors itself */
  outline: propTypes.bool,

  /** Which style of input to use */
  variant: propTypes.oneOf([DROPDOWN_VARIANT.BASIC, DROPDOWN_VARIANT.BASIC_LINE, DROPDOWN_VARIANT.BASIC_LINE_DASHED]),

  /** style overrides */
  customStyle: propTypes.shape({
    rootStyle: propTypes.object,
    // the Top level wrapper
    labelStyle: propTypes.object,
    // styling for labels
    dropdownInputStyle: propTypes.object,
    // styling for Dropdown Input items
    dropdownMenuStyle: propTypes.object // styling for Dropdown Menu items

  })
} : void 0;
Dropdown.defaultProps = {
  id: undefined,
  label: null,
  ariaRequired: undefined,
  ariaLabel: undefined,
  ariaLabelledBy: undefined,
  ariaDescribedBy: undefined,
  dropdownItems: [],
  placeholder: '',
  callouts: false,
  selected: 0,
  value: null,
  disabled: false,
  onChange: noop,
  errors: [],
  outline: false,
  variant: DROPDOWN_VARIANT.BASIC,
  customStyle: {
    rootStyle: {},
    labelStyle: {},
    dropdownInputStyle: {},
    dropdownMenuStyle: {}
  }
};
process.env.NODE_ENV !== "production" ? Dropdown.displayName = 'Dropdown' : void 0;

export default Dropdown;
